import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ProcessMenu } from '../components/ProcessMenu.js';
import { PaymentOptionsNav } from '../components/PaymentOptionsNav.js';
import {
  PROCESS_MENU_OPTIONS,
  makeProcessMenuHandler,
} from './process-menu-shared.js';
import { toast } from 'sonner';
import { api } from '../api/client.js';
import { useCustomer } from '../customer-context.js';
import { resolveActionBlockMessage } from '../lib/action-denied.js';
import { accountOptionLabel } from '../lib/account-label.js';

/**
 * SCRN 32 — Funds Transfer → non-notice internal transfer (CCF-69).
 *
 * Faithful port of the Payments-homescreen Figma "Transfer between accounts"
 * branch, scoped to the realized slice: move funds between a customer's OWN
 * eligible accounts. The agent reaches this screen having ALREADY selected
 * "Transfer between accounts" on the Payments screen, so per the CCF-69
 * acceptance criteria the From / To account dropdowns are presented DIRECTLY —
 * there is no second "payment option" dropdown to re-select the same intent.
 * The agent picks a From account and a To account (only eligible accounts are
 * listed — the gateway returns an error for an ineligible account, which the
 * screen surfaces); selecting an account reveals its "Account details" panel
 * (the fields the accounts list carries — which naturally differ by account
 * type), enters an amount, ticks the "Transfer amounts confirmed and scripting
 * completed" checkbox and clicks "Complete transfer"; on success a "Transfer
 * completed successfully" message shows.
 *
 * Backed by the Teller-ID-gated BFF via the generated typed client:
 *   - accounts: POST /api/customers/accounts
 *   - transfer: POST /api/payments/inter-account
 *
 * The notice-account force-withdrawal sub-flow (CCF-70) is ALSO built here now
 * that the R4 `notice/penalty/calculate` + `notice/penalty/apply` ops are
 * contracted: when a NOTICE account is selected as the From account the screen
 * switches to the force-withdrawal flow — the agent enters an amount and clicks
 * "Force Withdrawal", which calls calculate to PREVIEW the early-withdrawal
 * penalty; the penalty (or "No penalty" when below-minimum / withdrawal allowed)
 * is disclosed and MUST be confirmed with the customer via an explicit "Penalty
 * confirmed with customer and scripting completed" checkbox before the
 * state-changing "Complete force withdrawal" apply is enabled. The shared agent
 * banner lives in the app shell, so this screen renders only the `<main>`
 * content.
 *
 * Backed by the Teller-ID-gated BFF via the generated typed client:
 *   - penalty preview: POST /api/accounts/notice-penalty-calculate
 *   - force withdrawal: POST /api/accounts/notice-penalty-apply
 */

interface AccountRow {
  AccountNumber: string;
  AccountType: string;
  /**
   * Raw R4 `ProductType` code from the gateway `Associations_List` entry
   * (issue #75) — e.g. `CAA`, `TDA`, `SBA`, `LAA`, `HLA`, `PEP`, `CC`. Distinct
   * from the bucketed {@link AccountType} (CURRENT/DEPOSIT/LOAN/CARD): a Term
   * Loan (`LAA`) and a Mortgage (`HLA`/`PEP`) both bucket to LOAN, but only
   * `LAA` is a valid TO destination (issue #838). Passed through by the BFF
   * adapter as `GatewayProductType`.
   */
  GatewayProductType?: string;
  /** IBAN (or policy number) carried on the accounts-list response. */
  Iban?: string;
  Nickname?: string;
  Balance?: number;
  AvailableBalance?: number;
  Currency?: string;
  Status?: string;
  Signatories?: string | number;
  /** CCF-70: 'Y' on a notice/deposit account (drives the force-withdrawal flow). */
  NoticeAccountInd?: string;
}

/**
 * A single active notice on a notice/deposit account (issue #576 / CCF-90).
 * Sourced from the `notice/list` gateway op via `POST /api/accounts/notices`
 * (the same read the Notice-Information screen uses). Carries the maturity date
 * and the notice amount surfaced in the From-account details panel.
 */
interface NoticeRow {
  NoticeNumber?: string;
  Amount?: number;
  Currency?: string;
  MaturityDate?: string;
}

/** A per-notice penalty line from the calculate preview (CCF-70). */
interface PenaltyRow {
  NoticeNumber: string;
  PenaltyAmount: number;
  MaturityDate: string;
  SufficientFunds: boolean;
  BelowMinimumPenalty: boolean;
}

/** The calculated force-withdrawal penalty preview (CCF-70). */
interface PenaltyPreview {
  Found: boolean;
  WithdrawalAllowed?: boolean;
  TotalPenalty?: number;
  Currency?: string;
  AvailableNoticeAmount?: number;
  Penalties?: PenaltyRow[];
  ResultCode?: string;
  ResultMessage?: string;
}

/**
 * Issue #489 — the BFF's machine-readable result code for the app-level
 * withdrawal-hold block (kept in sync with `TRANSFER_HOLD_BLOCK_CODE` in
 * `server/src/api.ts`). The gateway is never called on a block; the block's
 * ResultMessage is @dylan219's exact advisory copy, shown verbatim (no code).
 */
const TRANSFER_HOLD_BLOCK_CODE = 'ACCOUNT_RESTRICTION';

const STATUS_LABEL: Record<string, string> = {
  ACTIVE: 'Active',
  DORMANT: 'Dormant',
  CLOSED: 'Closed',
};

/**
 * Issue #167 — authoritative numeric `ErrorNumber` → human-readable decline
 * reason for the `payment/inter-account` (transfer-between-accounts) flow.
 *
 * Per the Unibanks `InterAccountTransfer` reference the gateway returns the SAME
 * generic `ErrorMessage` — "Unable to process Transaction" — for EVERY business
 * decline; the specific reason is carried only in the numeric `ErrorNumber`. So
 * surfacing the gateway message verbatim shows the agent "143 — Unable to Process
 * Transaction" with no indication of WHY. This map turns the known codes into the
 * reference's documented meaning so the agent sees the real reason.
 *
 * NOTE on #167's premise: the tester reported code **143** as "insufficient
 * funds". It is NOT — per the reference 143 is "Daily Transfer Limit/Number
 * exceeded". The genuine insufficient-funds codes are **299** (cancel) and **302**
 * (override required). Mapping each to its OWN accurate reason (rather than
 * blanket-labelling 143 "insufficient funds") keeps the agent correctly informed.
 *
 * Source: requirements/unibanks-reference/Unibanks_InterAccountTransfer.yaml.
 */
const TRANSFER_CODE_REASON: Record<string, string> = {
  '143': 'Daily transfer limit or number of transfers exceeded.',
  '147': 'The from or to account is not linked to the cardholder.',
  '106': 'The transfer-from account is not available.',
  '105': 'The transfer-to account is not available.',
  '200': 'The account is dormant — transaction cancelled.',
  '299': 'Insufficient funds — transaction cancelled.',
  '302': 'Insufficient funds — a 090 override is required.',
  '506': 'Insufficient notice for withdrawal.',
  '507': 'Minimum notice not served for withdrawal — transaction cancelled.',
  '416': 'More than one signature is required for this transfer.',
  '110': 'Transactions are not allowed on a foreign-exchange account.',
  '446': 'Withdrawals are restricted to department users only.',
  // #249 — the remaining documented InterAccountTransfer ErrorNumbers
  // (override / hold / dormant / restriction / decline codes). Wording is taken
  // verbatim from each example's `summary` in
  // requirements/unibanks-reference/Unibanks_InterAccountTransfer.yaml, so the
  // agent sees the real decline reason rather than the bare "327 — Unable to
  // process transaction". Additive only (no change to existing mappings).
  '327':
    'The account is inactive or dormant — an 85 or 90 override is required.',
  '85': 'Lodgements are not permitted on loans at the approval stage.',
  '90': 'A 090 override is required to complete this transaction.',
  '305': 'ATM deposit hold — a 090 override is required.',
  '300': 'Cheque management hold — a 090 override is required.',
  '243': 'Interest has accrued — the account must be reactivated first.',
  '15': 'Transaction denied.',
  '180': 'Transfer is not permitted.',
  '163': 'A fixed-term balance remains on the account.',
  '206': 'The account owner is deceased.',
  '214': 'Signed documents are pending on the account.',
  '221': 'AML documentation is pending on the account.',
  '222': 'The account is on a CRA-status hold.',
  '233': 'Access to this account is restricted to Head Office.',
  '318': 'Unknown customer.',
  '319': 'The account is subject to legal proceedings.',
  '321': 'The account is suspended or written off.',
  '323': 'The account is in dispute.',
  '411': 'This is a suspense account.',
  '415': 'This suspense account accepts lodgements only.',
  '743': 'Inactive account — valid AML documents are required.',
  '917': 'Pay no withdrawal — a withdrawal cannot be made from this account.',
  '389':
    'This transaction can only be carried out at the customer’s own branch.',
  '502':
    'The transfer would take the destination account over its maximum balance.',
  // #284 (CCF-69) — ErrorNumber 20 was unmapped, so the agent saw the bare
  // generic "20 — Unable to process Transaction". Wording taken from the
  // `summary` of the `20` example in
  // requirements/unibanks-reference/Unibanks_InterAccountTransfer.yaml.
  '20': 'No deposits allowed.',
  // #376 / #377 — completing the map from the AUTHORITATIVE ErrorNumber table
  // in requirements/unibanks-reference/Unibanks_InterAccountTransfer.yaml
  // (the `ErrorNumber` schema block, lines ~1219-1293 + the per-example
  // `summary`s). This op's declines are NOT in API_Error_Codes.csv — that
  // catalogue has no inter-account API_Name — so the InterAccountTransfer
  // reference IS the authoritative catalogue for this flow. Every documented
  // code now resolves to its real reason, so an unmapped code can never again
  // show the bare "NN — Unable to process transaction" (the #284/#286 rule:
  // resolve from the catalogue, don't hand-add one code per ticket). #376 is
  // code 41 (max monthly lodgement on a regular saver); #377 is code 343
  // (lodgement not permitted — fixed-term deposit outside its lodgement window).
  // #636 — the code-41 message text is the authoritative CCF-69 acceptance
  // criteria wording (scenario 4), which supersedes the #376
  // Unibanks-summary-derived string. The "41 — …" code prefix is still applied
  // at render per the inline Code + Message convention (#82/#94).
  '41': 'The monthly lodgment limit on the account has been reached',
  '343': 'Lodgement not allowed on this account — transaction cancelled.',
  '0': 'No issues.',
  '9100': 'Standard header fields are not populated.',
  '9101': 'The customer number does not exist.',
  '9103': 'Invalid card number.',
  '153': 'The channel is closed.',
  '154': 'Invalid channel received.',
  '352': 'The from account is empty.',
  '353': 'The to account is empty.',
  '354': 'The from and to accounts cannot be the same.',
  '351': 'The amount is zero.',
  '505': 'The teller number does not exist, or the teller is disabled.',
  '508': 'Term loans with LPP must draw down the application amount.',
  '510': 'Cannot draw more than the approval withdrawal amount.',
  '408': 'The amount is below the minimum withdrawal.',
  '409': 'The resulting balance would be below the minimum balance.',
  '501': 'Withdrawals are not allowed for the first 90 days of the account.',
  '298': 'The amount exceeds the loan contract — contact the Advances team.',
  '232': 'The withdrawal amount exceeds your limit — an override is required.',
  '24': 'The amount is less than the minimum initial amount required.',
  '25': 'Deposits are restricted to department users only.',
  '324':
    'The amount exceeds the balance — an 85 override (ML/EM/ER) is required, otherwise cancel.',
  '407': 'The amount is below the minimum deposit.',
  '358': 'Partial redemption — a manual lodgement is required.',
  '342': 'No rate is held on the matrix for this account.',
  '114': 'Irish Life account — transaction cancelled.',
  '304': 'Overdraft set-off — a 090 override is required.',
  '320': 'Multiple warnings — a 090 override is required.',
  '912': 'Awaiting customer ID — no transactions allowed.',
  '915': 'Refer to the FCPU.',
  '922': 'AML exit — only DS users can transact on this account type.',
  '955': 'For area-office use only.',
  '957': 'Charged-off account — lodgements only.',
  // #499 — the notice-account force-withdrawal sub-flow (CCF-70) declines via the
  // `Apply notice penalty` / `Calculate Notice Penalty` gateway ops, whose
  // `ErrorNumber`s live in requirements/unibanks-reference/API_Error_Codes.csv
  // (rows for `Unibanks_ApplyNoticePenalty` / `Unibanks_CalculateNoticePenalty`).
  // The reporter hit **9327** ("Input amount is greater than account balance" —
  // trying to withdraw more than the notice account holds), which was unmapped, so
  // the agent saw only the bare generic "9327 — Unable to process transaction".
  // Mapping the WHOLE op's catalogue (not just 9327) means the next unmapped
  // notice-penalty code also resolves to its real reason (#284/#286). Wording is
  // taken verbatim from the catalogue's `Description` column. Codes already mapped
  // above (`153`, `154`, `9101`, `9103`) are not repeated.
  '9327': 'The amount is greater than the account balance.',
  '9328':
    'The amount is greater than the maximum withdrawal allowed on the bank control.',
  '9331': 'Notice check failed (tax-saver accounts only).',
  '9332':
    'Tax-saver accounts must not carry a notice number other than 00000000.',
  '9333': 'A notice number is mandatory when non-matured user notices exist.',
  '9334':
    'The notice number must be 00000000 when no non-matured user notices exist.',
  '9335': 'BelowMinimumPenalty must be either Y or N.',
  '9336': 'A penalty is mandatory when BelowMinimumPenalty is N.',
  '9337':
    'Penalty or notice validation mismatch when BelowMinimumPenalty is N.',
  '9338':
    'BelowMinimumPenalty is Y but the penalty exceeds the minimum penalty for this account type.',
  '9339':
    'The available notice amount is greater than the input amount — no need to force or break notice.',
  '9102': 'Invalid card-to-customer relation.',
  '9104': 'Invalid card type for the given online card.',
  '482': 'This is not a notice account.',
  '504': 'Invalid account type for the given account.',
  '289': 'No account-to-customer correlation.',
  '100': 'Account not found.',
  '87': 'Invalid teller number — the teller does not exist.',
};

/**
 * Issue #115 / #167 — build the inline failure text for the funds-transfer
 * "Complete transfer" action. Surfaces the gateway's own Code + Message (e.g.
 * "147 — Unable to Process Transaction" — the real PTSB staging gateway's
 * `payment/inter-account` decline, confirmed by live replay) so the agent sees
 * the real decline beside the button rather than a transient generic toast, and
 * — when the numeric code is a KNOWN `InterAccountTransfer` `ErrorNumber` —
 * appends the reference's human-readable reason (#167), since the gateway message
 * itself is the generic "Unable to process Transaction" for every decline.
 * Mirrors the authoritative #80/#82/#94/#104 pattern (PostalAddressScreen) and
 * tolerates BOTH outcome shapes the BFF can return:
 *
 *  - a 200 with `Success:false` carrying `ResultCode`/`ResultMessage` (the
 *    gateway's `SuccessErrorInd:'N'` business-error, mapped by `mapWriteOutcome`);
 *  - a 4xx `gateway_error` body `{ gateway: { code, message } }` (a true HTTP
 *    rejection — e.g. the real staging gateway's JWS-wrapped 400).
 *
 * `0000` is the internal success code and is never shown.
 *
 * `transferErrorDetail` returns just the "<code> — <description>" detail (or ''
 * when the gateway gave neither a code nor a message); `formatTransferError`
 * wraps it, falling back to a generic line for the empty case (e.g. a transport
 * failure). The `''`-on-empty split lets the CCF-70 partial-failure message
 * APPEND the actual decline code/description without a stray generic line (#488).
 */
export function transferErrorDetail(body: unknown): string {
  const b = (body ?? {}) as {
    ResultCode?: string;
    ResultMessage?: string;
    gateway?: { code?: string | number; message?: string };
  };
  const rawCode = b.gateway?.code ?? b.ResultCode;
  const code = rawCode === undefined ? '' : String(rawCode).trim();
  const message = (b.gateway?.message ?? b.ResultMessage)?.trim();
  // Issue #489 — the app-level withdrawal-hold block (`ACCOUNT_RESTRICTION`) is
  // NOT a gateway ErrorNumber; its ResultMessage is @dylan219's exact advisory
  // copy ("Unable to complete transfer due to the account restrictions"), shown
  // verbatim with NO code prefix (unlike a gateway business decline).
  const showCode = code && code !== '0000' && code !== TRANSFER_HOLD_BLOCK_CODE;
  // Known InterAccountTransfer ErrorNumber → its documented, human-readable
  // reason (#167). The gateway's own message is the generic "Unable to process
  // Transaction" for every code, so the reason is what the agent actually needs.
  const reason = showCode ? TRANSFER_CODE_REASON[code] : undefined;
  if (showCode && reason) return `${code} — ${reason}`;
  if (showCode && message) return `${code} — ${message}`;
  if (message) return message;
  if (showCode) return `Transfer failed (code ${code}).`;
  // No code and no message — nothing meaningful to surface. The caller decides
  // whether to fall back to a generic line (formatTransferError) or omit the
  // detail entirely (#488 partial-failure append).
  return '';
}

export function formatTransferError(body: unknown): string {
  return (
    transferErrorDetail(body) || 'Unable to complete transfer. Please retry.'
  );
}

/** Format a numeric balance as EUR, defensively (returns '—' when absent). */
function formatMoney(value: number | undefined, currency = 'EUR'): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '—';
  try {
    return new Intl.NumberFormat('en-IE', {
      style: 'currency',
      currency,
    }).format(value);
  } catch {
    return `${currency} ${value.toFixed(2)}`;
  }
}

/**
 * Per-account "Account details" panel (CCF-69 / Payments-homescreen Figma).
 * Shown once an account is picked in the adjacent dropdown. Only the fields the
 * accounts-list response carries are rendered — these naturally differ by
 * account type (e.g. a deposit account may omit Signatories), satisfying the
 * "shows different information depending on the account type" expectation
 * without inventing fields the list endpoint does not return.
 */
function AccountDetails({
  account,
  notice,
}: {
  account: AccountRow;
  /**
   * Issue #576 — when this account is a notice account selected on the FROM
   * side, its active notices (from `notice/list`) drive the "Notice maturity
   * date" dropdown + "Notice amount" rows. Absent for the TO side and for
   * non-notice accounts.
   */
  notice?: {
    notices: NoticeRow[];
    selectedIndex: number;
    onSelect: (index: number) => void;
  };
}): React.JSX.Element {
  // Issue #576 — the "Account name" row is intentionally NOT shown for any
  // account type (tester ruling: "there is no need to have the account name
  // listed in the account details section for any account types").
  const rows: { label: string; value: string }[] = [
    {
      label: 'IBAN',
      value: account.Iban ?? account.AccountNumber ?? '—',
    },
  ];
  if (account.Signatories !== undefined)
    rows.push({ label: 'Signatories', value: String(account.Signatories) });
  if (account.Status)
    rows.push({
      label: 'Status',
      value: STATUS_LABEL[account.Status] ?? account.Status,
    });
  rows.push({
    label: 'Account balance',
    value: formatMoney(account.Balance, account.Currency),
  });
  rows.push({
    label: 'Available balance',
    value: formatMoney(account.AvailableBalance, account.Currency),
  });

  // Issue #576 — the selected notice (for the "Notice amount" row). The
  // dropdown only shows each notice's maturity date; the amount reflects the
  // selection. When the account has no active notices the dropdown shows
  // "None" (tester ruling) and there is no amount to show.
  const hasNotices = !!notice && notice.notices.length > 0;
  // Issue #633 (CCF-70) — an interactive dropdown is only warranted when there
  // is more than one notice to choose between. With exactly one notice the
  // maturity date is rendered as plain, non-interactive text (the notice amount
  // is still shown in its own row below); with none we keep the "None" ruling.
  const hasMultipleNotices = !!notice && notice.notices.length > 1;
  const selectedNotice = hasNotices
    ? notice.notices[notice.selectedIndex]
    : undefined;

  return (
    <div className="mt-4">
      <h3 className="text-[16px] font-['Arial',sans-serif] font-bold text-[#1d252c] mb-3">
        Account details
      </h3>
      <div className="border border-[#dad7d3]">
        <table className="w-full">
          <tbody>
            {rows.map((r) => (
              <tr
                key={r.label}
                className="border-b border-[#dad7d3] last:border-b-0"
              >
                <td className="px-3 py-2 text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c] bg-[#f8f9fa] w-[45%]">
                  {r.label}
                </td>
                <td className="px-3 py-2 text-[14px] font-['Arial',sans-serif] text-[#1d252c]">
                  {r.value}
                </td>
              </tr>
            ))}
            {/* Issue #576 — Notice accounts on the FROM side surface their
                active notices: a "Notice maturity date" dropdown listing each
                notice's maturity date (or "None"), and the "Notice amount" of
                the selected notice. */}
            {notice && (
              <>
                <tr className="border-b border-[#dad7d3]">
                  <td className="px-3 py-2 text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c] bg-[#f8f9fa] w-[45%]">
                    Notice maturity date
                  </td>
                  <td className="px-3 py-2 text-[14px] font-['Arial',sans-serif] text-[#1d252c]">
                    {hasMultipleNotices ? (
                      <select
                        aria-label="Notice maturity date"
                        value={notice.selectedIndex}
                        onChange={(e) =>
                          notice.onSelect(Number(e.target.value))
                        }
                        className="w-full px-2 py-1 border border-[#d1d5db] rounded-md bg-white font-['Arial',sans-serif] text-[14px] text-[#1d252c] focus:outline-none focus:border-[#005fcc] focus:ring-2 focus:ring-[#005fcc]/20"
                      >
                        {notice.notices.map((n, i) => (
                          <option key={n.NoticeNumber ?? i} value={i}>
                            {`Notice ${i + 1} - ${n.MaturityDate || '—'}`}
                          </option>
                        ))}
                      </select>
                    ) : hasNotices ? (
                      // Issue #633 — exactly one notice: render the single
                      // maturity date as plain, non-interactive text (no
                      // dropdown to choose from). The notice amount row below
                      // still shows this notice's amount.
                      (selectedNotice?.MaturityDate ?? '—')
                    ) : (
                      'None'
                    )}
                  </td>
                </tr>
                <tr className="border-b border-[#dad7d3] last:border-b-0">
                  <td className="px-3 py-2 text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c] bg-[#f8f9fa] w-[45%]">
                    Notice amount
                  </td>
                  <td className="px-3 py-2 text-[14px] font-['Arial',sans-serif] text-[#1d252c]">
                    {selectedNotice
                      ? formatMoney(
                          selectedNotice.Amount,
                          selectedNotice.Currency ?? account.Currency,
                        )
                      : '—'}
                  </td>
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * Account types eligible on the FROM side of an internal transfer. Per the
 * CCF-69 technical note only eligible accounts are listed in the dropdowns; the
 * customer can only transfer OUT of a CURRENT or DEPOSIT account. LOAN and CARD
 * accounts are excluded from FROM. (The gateway also rejects an ineligible
 * account, so this is a UI convenience, not the authoritative gate.)
 */
const FROM_ELIGIBLE_ACCOUNT_TYPES: ReadonlySet<string> = new Set([
  'CURRENT',
  'DEPOSIT',
]);

/**
 * The TO side additionally allows a Term Loan (`LAA`) as a destination — an
 * agent can pay INTO a loan but never transfer OUT of one (@dylan219, issue
 * #838: "include all account Type of LAA in the to drop down but not in the
 * from drop down"). Keyed on the raw `GatewayProductType` code, NOT the bucketed
 * `AccountType`: only `LAA` qualifies, not the mortgage product types (`HLA`/
 * `PEP`) that share the LOAN bucket.
 */
const TO_ELIGIBLE_PRODUCT_TYPES: ReadonlySet<string> = new Set(['LAA']);

/** Accessible dropdown, faithful to the export's listbox pattern. */
function Dropdown({
  id,
  label,
  value,
  placeholder,
  options,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  placeholder: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const selected = options.find((o) => o.value === value);

  useEffect(() => {
    function onClickOutside(e: MouseEvent): void {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false);
    }
    if (open) {
      document.addEventListener('mousedown', onClickOutside);
      return () => document.removeEventListener('mousedown', onClickOutside);
    }
    return undefined;
  }, [open]);

  return (
    <div className="flex flex-col gap-2" ref={ref}>
      <label
        htmlFor={id}
        // Red (PTSB orange #FF703E) underline beneath the label, per the
        // Payments-homescreen Figma (issue #58). `w-fit` keeps the border under
        // the wording only rather than stretching the full flex column width.
        className="w-fit pb-1 border-b-2 border-[#FF703E] text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c]"
      >
        {label}
      </label>
      <div className="relative">
        <button
          id={id}
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-haspopup="listbox"
          aria-expanded={open}
          className="w-full px-4 py-3 border-2 border-[#dad7d3] rounded-md font-['Arial',sans-serif] text-[16px] text-[#1d252c] bg-white text-left transition-all focus:outline-none focus:border-[#005fcc] focus:ring-4 focus:ring-[#005fcc]/20 hover:border-[#979c9f] flex items-center justify-between"
        >
          <span className={selected ? '' : 'text-[#6b7280]'}>
            {selected ? selected.label : placeholder}
          </span>
          <svg
            className={`size-5 transition-transform ${open ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>
        {open && (
          <ul
            role="listbox"
            aria-label={label}
            className="absolute top-full mt-2 w-full bg-white border-2 border-[#dad7d3] rounded-md shadow-lg z-[100] max-h-[240px] overflow-y-auto"
          >
            {options.map((option) => (
              <li
                key={option.value || 'placeholder'}
                role="option"
                aria-selected={value === option.value}
                tabIndex={0}
                onClick={() => {
                  onChange(option.value);
                  setOpen(false);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onChange(option.value);
                    setOpen(false);
                  }
                }}
                className={`px-4 py-3 cursor-pointer font-['Arial',sans-serif] text-[16px] text-[#1d252c] hover:bg-[#f3f4f6] focus:outline-none focus:bg-[#e5e7eb] ${
                  value === option.value ? 'bg-[#e5e7eb] font-bold' : ''
                }`}
              >
                {option.label}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export function FundsTransferScreen(): React.JSX.Element {
  const { customer } = useCustomer();
  const navigate = useNavigate();

  const customerNumber = customer.cif ?? '';
  const tellerId = customer.tellerId ?? '';
  const hasContext = !!customerNumber && /^\d{4}$/.test(tellerId);

  // Shared process-menu navigation handler (issue #45) — wires each option to
  // its existing, RBAC-gated route.
  const onProcessSelect = makeProcessMenuHandler(navigate);

  const [accounts, setAccounts] = useState<AccountRow[]>([]);
  const [fromAccount, setFromAccount] = useState('');
  const [toAccount, setToAccount] = useState('');
  const [amount, setAmount] = useState('');
  const [amountError, setAmountError] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  // Issue #115 — persistent inline transfer outcome shown BESIDE the "Complete
  // transfer" button (a toast alone auto-dismisses and is easy to miss). A
  // failed transfer surfaces the gateway's Code + Message verbatim (recoverable
  // — the agent can correct the accounts/amount and retry); the `completed`
  // success block already renders the confirmation beside the button (#82/#94).
  const [transferError, setTransferError] = useState('');

  // --- Force-withdrawal (CCF-70) sub-flow state ---------------------------
  // The calculated penalty preview (null until "Force Withdrawal" is clicked),
  // whether a calculate is in flight, the "penalty confirmed with customer"
  // gate, and the completed-withdrawal flag. These are DISTINCT from the
  // non-notice transfer state above — a notice From account routes through this
  // calculate→confirm→apply path, never the plain /payments/inter-account call.
  const [preview, setPreview] = useState<PenaltyPreview | null>(null);
  const [calculating, setCalculating] = useState(false);
  const [penaltyConfirmed, setPenaltyConfirmed] = useState(false);
  const [fwCompleted, setFwCompleted] = useState(false);
  const [fwError, setFwError] = useState('');

  // --- Notice-account display (issue #576) --------------------------------
  // The active notices on the FROM account when it is a notice account, plus
  // the currently-selected notice (drives the "Notice maturity date" dropdown
  // and the "Notice amount" row in the FROM account-details panel). Fetched
  // from `notice/list` via POST /api/accounts/notices when a notice FROM
  // account is chosen. `noticesLoaded` distinguishes "no notices on this
  // account" (show "None") from "not yet loaded".
  const [fromNotices, setFromNotices] = useState<NoticeRow[]>([]);
  const [noticesLoaded, setNoticesLoaded] = useState(false);
  const [selectedNoticeIndex, setSelectedNoticeIndex] = useState(0);

  useEffect(() => {
    let cancelled = false;
    // Clear the PRIOR customer's data immediately when the customer/teller
    // changes (or context clears), so one customer's account names/last-four
    // can never linger visible/selectable while the next load is in flight.
    setAccounts([]);
    setFromAccount('');
    setToAccount('');
    setAmount('');
    setAmountError('');
    setConfirmed(false);
    setCompleted(false);
    setError('');
    setTransferError('');
    setPreview(null);
    setPenaltyConfirmed(false);
    setFwCompleted(false);
    setFwError('');
    setFromNotices([]);
    setNoticesLoaded(false);
    setSelectedNoticeIndex(0);
    async function load(): Promise<void> {
      if (!hasContext) {
        setError('No verified customer in context. Complete ID&V first.');
        return;
      }
      try {
        const { data, error: apiError } = await api.POST(
          '/customers/accounts',
          {
            params: { header: { 'X-Teller-Id': tellerId } },
            body: { customerNumber },
          },
        );
        if (cancelled) return;
        if (apiError || !data) {
          setError('Could not load accounts.');
          return;
        }
        // Clear any prior load error (e.g. a transient failure, or the
        // "complete ID&V first" message from before the context was verified)
        // now that the accounts have loaded successfully.
        setError('');
        setAccounts((data.Accounts ?? []) as unknown as AccountRow[]);
      } catch {
        if (!cancelled) setError('Request failed.');
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerNumber, tellerId]);

  // Money movement: the confirmation must apply to the EXACT current account
  // pair + amount. If any of them change after the agent ticked confirm, drop
  // the confirmation so they must re-confirm the new details.
  useEffect(() => {
    setConfirmed(false);
    // Clear any prior inline transfer outcome when the details change — a stale
    // decline must not linger against a now-different account pair / amount.
    setTransferError('');
    // A change to the accounts/amount INVALIDATES a calculated penalty (it was
    // computed for the old details), so the agent must re-preview + re-confirm
    // before any force withdrawal can complete. Money movement must bind to the
    // exact previewed details.
    setPreview(null);
    setPenaltyConfirmed(false);
    setFwError('');
  }, [fromAccount, toAccount, amount]);

  function onAmountChange(value: string): void {
    setAmount(value);
    const v = value.trim();
    // Require a strictly-positive decimal — reject 0/0.00 inline rather than
    // letting the contract/BFF bounce it as a generic failure (they also reject
    // it; this is the friendly first line).
    if (v && (!/^\d+(\.\d{1,2})?$/.test(v) || !(Number.parseFloat(v) > 0))) {
      setAmountError('Enter a valid amount greater than 0 (e.g. 100.00).');
    } else {
      setAmountError('');
    }
  }

  // FROM: CURRENT + DEPOSIT only. TO: the same, plus Term Loan (LAA)
  // destinations (issue #838).
  const fromEligibleAccounts = accounts.filter((a) =>
    FROM_ELIGIBLE_ACCOUNT_TYPES.has(a.AccountType),
  );
  const toEligibleAccounts = accounts.filter(
    (a) =>
      FROM_ELIGIBLE_ACCOUNT_TYPES.has(a.AccountType) ||
      TO_ELIGIBLE_PRODUCT_TYPES.has(a.GatewayProductType ?? ''),
  );

  // The selected account rows — drive each side's "Account details" panel.
  const fromAccountRow = accounts.find((a) => a.AccountNumber === fromAccount);
  const toAccountRow = accounts.find((a) => a.AccountNumber === toAccount);

  function accountLabel(a: AccountRow): string {
    return accountOptionLabel(a);
  }

  /** True if the account is a notice/deposit account (CCF-70). */
  function isNoticeAccount(a: AccountRow | undefined): boolean {
    return (
      !!a && (a.NoticeAccountInd === 'Y' || /notice/i.test(a.Nickname ?? ''))
    );
  }

  // A NOTICE account selected as the FROM side routes through the CCF-70
  // force-withdrawal flow (calculate → confirm penalty → apply), NEVER the plain
  // /payments/inter-account path (which bypasses the penalty rules). Only the
  // FROM side matters — a force withdrawal is FROM a notice account.
  const forceWithdrawal = isNoticeAccount(fromAccountRow);

  // Issue #576 — when a NOTICE account is selected as the FROM side, load its
  // active notices (`notice/list` via POST /api/accounts/notices) so the
  // account-details panel can show the "Notice maturity date" dropdown + the
  // "Notice amount". Only the FROM side is a notice source (a withdrawal is
  // FROM a notice account). A non-notice FROM account clears any prior notices.
  useEffect(() => {
    let cancelled = false;
    setFromNotices([]);
    setNoticesLoaded(false);
    setSelectedNoticeIndex(0);
    if (!forceWithdrawal || !fromAccount || !hasContext) return;
    async function loadNotices(): Promise<void> {
      try {
        const { data, error: apiError } = await api.POST('/accounts/notices', {
          params: { header: { 'X-Teller-Id': tellerId } },
          body: { customerNumber, accountNumber: fromAccount },
        });
        if (cancelled) return;
        // A gateway "not a notice account / unable to process" outcome
        // (`Found:false`) or a failed call leaves the list empty → the panel
        // shows "None" rather than a hard error (the force-withdrawal flow
        // still surfaces any real decline on its own calculate call).
        const result = (data ?? {}) as {
          Found?: boolean;
          Notices?: NoticeRow[];
        };
        setFromNotices(
          apiError || result.Found === false ? [] : (result.Notices ?? []),
        );
        setNoticesLoaded(true);
      } catch {
        if (!cancelled) {
          setFromNotices([]);
          setNoticesLoaded(true);
        }
      }
    }
    void loadNotices();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromAccount, forceWithdrawal, customerNumber, tellerId]);

  // Both sides chosen, distinct, amount entered + valid → ready to act. For a
  // NON-notice transfer this gates the confirm checkbox; for a force withdrawal
  // it gates the "Force Withdrawal" (calculate) button.
  const ready =
    !!fromAccount &&
    !!toAccount &&
    fromAccount !== toAccount &&
    !!amount.trim() &&
    !amountError;

  // The applicable penalty row for the apply call. Prefer an APPLIED penalty
  // (BelowMinimumPenalty:false) — that is the one that actually incurs a charge;
  // fall back to the first row (a below-minimum / waived penalty still commits
  // the withdrawal, echoing the row per the R4 contract).
  const penaltyRows = preview?.Penalties ?? [];
  const chosenPenalty =
    penaltyRows.find((p) => !p.BelowMinimumPenalty) ?? penaltyRows[0];

  /**
   * "Force Withdrawal" — call the penalty calculate PREVIEW (CCF-70). No funds
   * move. On success the penalty breakdown is shown for the agent to confirm
   * with the customer; a decline surfaces the gateway code/message inline.
   */
  async function calculatePenalty(): Promise<void> {
    if (!ready || calculating) return;
    setCalculating(true);
    setFwError('');
    setPreview(null);
    setPenaltyConfirmed(false);
    try {
      const {
        data,
        error: apiError,
        response,
      } = await api.POST('/accounts/notice-penalty-calculate', {
        params: { header: { 'X-Teller-Id': tellerId } },
        body: {
          customerNumber,
          accountNumber: fromAccount,
          // CCF-70: the destination the withdrawn funds move to. Bound into the
          // server-side preview so apply commits against the exact destination.
          toAccountNumber: toAccount,
          amount: amount.trim(),
        },
      });
      if (apiError || !data) {
        const message =
          resolveActionBlockMessage(response.status, apiError) ??
          (response.status === 403
            ? 'Forbidden: check your Teller ID and permissions.'
            : 'Could not calculate the penalty. Please retry.');
        setFwError(message);
        toast.error(message);
        return;
      }
      const p = data as unknown as PenaltyPreview;
      if (p.Found === false) {
        const message = formatTransferError(p);
        setFwError(message);
        toast.error(message);
        return;
      }
      setPreview(p);
    } catch {
      const message = 'Could not calculate the penalty. Please retry.';
      setFwError(message);
      toast.error(message);
    } finally {
      setCalculating(false);
    }
  }

  /**
   * "Complete force withdrawal" — the STATE-CHANGING apply (CCF-70). Gated on a
   * calculated preview AND the explicit "penalty confirmed with customer" tick.
   * Echoes the chosen calculate row (penalty / notice number / below-minimum)
   * per the R4 contract.
   */
  async function completeForceWithdrawal(): Promise<void> {
    if (!preview || !penaltyConfirmed || submitting) return;
    setSubmitting(true);
    setFwError('');
    try {
      const {
        data,
        error: apiError,
        response,
      } = await api.POST('/accounts/notice-penalty-apply', {
        params: { header: { 'X-Teller-Id': tellerId } },
        body: {
          customerNumber,
          accountNumber: fromAccount,
          // CCF-70: the destination the withdrawn funds move to — must match the
          // destination bound into the server-side preview (money-movement
          // control); a mismatch fails closed (409).
          toAccountNumber: toAccount,
          amount: amount.trim(),
          penalty: (chosenPenalty?.PenaltyAmount ?? 0).toFixed(2),
          noticeNumber: chosenPenalty?.NoticeNumber ?? '',
          belowMinimumPenalty: chosenPenalty?.BelowMinimumPenalty ?? false,
        },
      });
      if (
        apiError ||
        !data ||
        (data as { Success?: boolean }).Success !== true
      ) {
        // CCF-70 partial failure (Dylan #218): the notice was broken + penalty
        // charged but the follow-up transfer did not complete. There is no
        // rollback — surface the server's explicit "complete the transfer
        // manually" message verbatim so the agent knows funds have NOT moved and
        // must not simply retry (a retry re-applies nothing — the preview is
        // consumed — and the notice is already broken).
        const body = (apiError ?? data) as {
          penaltyApplied?: boolean;
          transferComplete?: boolean;
          message?: string;
        };
        let message: string;
        if (body?.penaltyApplied === true && body?.transferComplete === false) {
          // CCF-70 partial failure (Dylan #218): the friendly "complete
          // manually" wording is authoritative and stays, but #488 asks that the
          // ACTUAL gateway error code + description for the failed transfer be
          // appended at the end (the body carries the failed inter-account
          // ResultCode/ResultMessage via the BFF's `...transfer` spread), so the
          // agent sees the real decline reason, not just the friendly sentence.
          const base =
            body.message ??
            'The notice was broken and the penalty applied, but the transfer did not complete. Please complete the transfer manually.';
          const detail = transferErrorDetail(body);
          message = detail ? `${base} (${detail})` : base;
        } else {
          const blocked = resolveActionBlockMessage(response.status, apiError);
          if (blocked !== null) {
            message = blocked;
          } else if (response.status === 403) {
            message = 'Forbidden: check your Teller ID and permissions.';
          } else {
            message = formatTransferError(apiError ?? data);
          }
        }
        setFwError(message);
        toast.error(message);
        return;
      }
      setFwCompleted(true);
      toast.success('Force withdrawal completed');
    } catch {
      const message = 'Unable to complete force withdrawal. Please retry.';
      setFwError(message);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  }

  async function submit(): Promise<void> {
    if (!ready || !confirmed || forceWithdrawal) return;
    setSubmitting(true);
    setError('');
    setTransferError('');
    try {
      const {
        data,
        error: apiError,
        response,
      } = await api.POST('/payments/inter-account', {
        params: { header: { 'X-Teller-Id': tellerId } },
        body: {
          customerNumber,
          fromAccountNumber: fromAccount,
          toAccountNumber: toAccount,
          amount: amount.trim(),
        },
      });
      if (
        apiError ||
        !data ||
        (data as { Success?: boolean }).Success !== true
      ) {
        // Issue #248 — the RBAC *action* denial: `requirePermission` refuses a
        // blocked action with 403 `{ error: 'forbidden' }` (e.g. a restricted
        // role attempting this write on an OFFLINE journey, per increment 1).
        // That case shows @dylan219's authoritative verbatim copy inline beside
        // the button — never a raw 403 or a generic error. This is distinct from
        // the gateway business-decline (#80/#82/#115) and from the Teller-ID gate
        // (`invalid_teller_id`), which keeps its own message.
        //
        // Issue #115: otherwise surface the failure as a PERSISTENT inline result
        // beside the "Complete transfer" button (the toast alone auto-dismisses
        // and is easy to miss). Show the gateway's own Code + Message verbatim —
        // e.g. "147 — Unable to Process Transaction" — so the agent can act on
        // the real reason and retry. `apiError` carries the BFF `gateway_error`
        // body on a true 4xx; `data` carries the SuccessErrorInd:'N' 200 result.
        const message =
          resolveActionBlockMessage(response.status, apiError) ??
          (response.status === 403
            ? 'Forbidden: check your Teller ID and permissions.'
            : formatTransferError(apiError ?? data));
        setTransferError(message);
        toast.error(message);
        return;
      }
      setCompleted(true);
      toast.success('Transfer Submitted');
    } catch {
      const message = 'Unable to complete transfer. Please retry.';
      setTransferError(message);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main
      className="max-w-[1800px] mx-auto px-12 py-12"
      aria-labelledby="ft-heading"
    >
      <div className="mb-8">
        <a
          href="/dashboard"
          onClick={(e) => {
            e.preventDefault();
            navigate('/dashboard');
          }}
          className="inline-flex items-center gap-2 text-[16px] font-['Arial',sans-serif] text-[#FF703E] underline hover:text-[#e55a2a] focus:outline-none focus:ring-2 focus:ring-[#005fcc] focus:ring-offset-2 rounded"
        >
          &lt; Back to customer dashboard
        </a>
      </div>

      <div className="flex items-center justify-between gap-8 mb-8 flex-wrap">
        <h1
          id="ft-heading"
          className="text-[28px] font-['Arial',sans-serif] font-bold text-[#1d252c] mb-8"
        >
          Funds transfer
        </h1>
        {/* Process-menu navigation (issue #45): jump to a related process.
            Pure navigation chooser — each route stays independently RBAC-gated
            (deny-by-default). */}
        <ProcessMenu
          options={PROCESS_MENU_OPTIONS}
          onSelect={onProcessSelect}
        />
      </div>

      {/* Persistent payment-options selector (issue #116): switch directly
          between the four payment options without returning to the dashboard.
          Placed on the LEFT below the heading to match Bill Payments and the
          sibling payment screens (issue #261). */}
      <div className="mb-6 flex gap-6 flex-wrap items-start">
        <PaymentOptionsNav current="Transfer between accounts" />
      </div>

      {error && (
        <div
          role="alert"
          className="mb-8 p-4 bg-white border border-[#dc2626] font-['Arial',sans-serif] text-[14px] text-[#1d252c]"
        >
          {error}
        </div>
      )}

      {/* The agent has already selected "Transfer between accounts" on the
          Payments screen, so per CCF-69 the From / To account dropdowns are
          presented DIRECTLY here — there is no second payment-option dropdown
          to re-select the same intent. */}
      <div>
        <div className="grid grid-cols-2 gap-6 mb-6 max-w-[900px]">
          {/* From account */}
          <div className="border-2 border-[#dad7d3] bg-white p-6">
            <Dropdown
              id="from-account"
              label="From account"
              placeholder="Select account"
              value={fromAccount}
              onChange={(v) => {
                setFromAccount(v);
                setCompleted(false);
              }}
              options={fromEligibleAccounts.map((a) => ({
                value: a.AccountNumber,
                label: accountLabel(a),
              }))}
            />
            {fromAccountRow && (
              <AccountDetails
                account={fromAccountRow}
                // Issue #576 — a notice FROM account shows its active notices
                // (maturity-date dropdown + amount). The dropdown shows "None"
                // once the notice list has loaded and is empty; while loading
                // the notice rows are held back (no `notice` prop) to avoid a
                // flash of "None".
                notice={
                  forceWithdrawal && noticesLoaded
                    ? {
                        notices: fromNotices,
                        selectedIndex: selectedNoticeIndex,
                        onSelect: setSelectedNoticeIndex,
                      }
                    : undefined
                }
              />
            )}
          </div>

          {/* To account */}
          <div className="border-2 border-[#dad7d3] bg-white p-6">
            <Dropdown
              id="to-account"
              label="To account"
              placeholder="Select account"
              value={toAccount}
              onChange={(v) => {
                setToAccount(v);
                setCompleted(false);
              }}
              options={toEligibleAccounts.map((a) => ({
                value: a.AccountNumber,
                label: accountLabel(a),
              }))}
            />
            {toAccountRow && <AccountDetails account={toAccountRow} />}
          </div>
        </div>

        {/* Same-account guard. */}
        {fromAccount && toAccount && fromAccount === toAccount && (
          <p
            className="mb-6 text-[14px] font-['Arial',sans-serif] text-[#dc2626] m-0"
            role="alert"
          >
            The from and to accounts must be different.
          </p>
        )}

        {/* Force-withdrawal notice banner (CCF-70): a notice From account is
            handled by the penalty calculate → confirm → apply flow below, not
            the plain transfer path. */}
        {forceWithdrawal && fromAccount && (
          <div
            className="mb-6 max-w-[700px] border-2 border-[#FF703E] bg-[#fff7f3] p-4"
            role="status"
          >
            <p className="text-[15px] font-['Arial',sans-serif] text-[#1d252c] m-0">
              This is a <strong>notice account</strong>. A withdrawal may incur
              an early-withdrawal penalty — click{' '}
              <strong>Force Withdrawal</strong> to calculate it, then confirm
              the charge with the customer before completing.
            </p>
          </div>
        )}

        {/* Amount. */}
        {fromAccount && toAccount && fromAccount !== toAccount && (
          <div className="max-w-[535px] mb-6">
            <div className="border-2 border-[#dad7d3] bg-[#f8f9fa] p-6">
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="transfer-amount"
                  className="text-[18px] font-['Arial',sans-serif] font-bold text-[#1d252c]"
                >
                  Amount
                </label>
                <input
                  id="transfer-amount"
                  type="text"
                  value={amount}
                  onChange={(e) => onAmountChange(e.target.value)}
                  placeholder="€00.00"
                  aria-invalid={!!amountError}
                  className={`px-4 py-2 border rounded-md bg-white font-['Arial',sans-serif] text-[16px] text-[#1d252c] placeholder:text-[#9ca3af] focus:outline-none focus:border-[#005fcc] focus:ring-4 focus:ring-[#005fcc]/20 ${
                    amountError ? 'border-[#dc2626]' : 'border-[#d1d5db]'
                  }`}
                />
                <div className="min-h-[18px]">
                  {amountError && (
                    <p
                      className="text-[14px] font-['Arial',sans-serif] text-[#dc2626] m-0"
                      role="alert"
                    >
                      {amountError}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ------- Force-withdrawal (CCF-70) calculate → confirm → apply ------- */}
        {forceWithdrawal && ready && !fwCompleted && (
          <div className="mb-6 max-w-[700px]">
            {/* Step 1 — calculate the penalty (preview; no funds move). */}
            {!preview && (
              <button
                type="button"
                disabled={calculating}
                onClick={() => void calculatePenalty()}
                className="px-6 py-3 bg-[#FF703E] text-[#1D252C] rounded-md font-['Arial',sans-serif] font-bold text-[16px] hover:bg-[#e55a2a] focus:outline-none focus:ring-4 focus:ring-offset-2 focus:ring-[#005fcc] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {calculating ? 'Calculating…' : 'Force Withdrawal'}
              </button>
            )}

            {/* Step 2 — disclose the calculated penalty for confirmation. */}
            {preview && (
              <div
                id="penalty-preview"
                className="border-2 border-[#dad7d3] bg-white p-6"
                role="status"
                aria-live="polite"
              >
                <h3 className="text-[16px] font-['Arial',sans-serif] font-bold text-[#1d252c] mb-3">
                  Early-withdrawal penalty
                </h3>
                {preview.WithdrawalAllowed ? (
                  <p className="text-[15px] font-['Arial',sans-serif] text-[#16a34a] m-0 mb-3">
                    No notice needs to be broken — this withdrawal is allowed
                    without a penalty.
                  </p>
                ) : (preview.TotalPenalty ?? 0) > 0 ? (
                  <p className="text-[18px] font-['Arial',sans-serif] font-bold text-[#1d252c] m-0 mb-3">
                    Penalty to be applied:{' '}
                    {formatMoney(preview.TotalPenalty, preview.Currency)}
                  </p>
                ) : (
                  <p className="text-[15px] font-['Arial',sans-serif] text-[#16a34a] m-0 mb-3">
                    No penalty applies to this withdrawal.
                  </p>
                )}
                {penaltyRows.length > 0 && (
                  <table className="w-full border border-[#dad7d3] mb-4">
                    <thead>
                      <tr className="bg-[#f8f9fa]">
                        <th className="px-3 py-2 text-left text-[13px] font-['Arial',sans-serif] font-bold text-[#1d252c]">
                          Notice number
                        </th>
                        <th className="px-3 py-2 text-left text-[13px] font-['Arial',sans-serif] font-bold text-[#1d252c]">
                          Maturity date
                        </th>
                        <th className="px-3 py-2 text-left text-[13px] font-['Arial',sans-serif] font-bold text-[#1d252c]">
                          Penalty
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {penaltyRows.map((p) => (
                        <tr
                          key={p.NoticeNumber}
                          className="border-t border-[#dad7d3]"
                        >
                          <td className="px-3 py-2 text-[14px] font-['Arial',sans-serif] text-[#1d252c]">
                            {p.NoticeNumber}
                          </td>
                          <td className="px-3 py-2 text-[14px] font-['Arial',sans-serif] text-[#1d252c]">
                            {p.MaturityDate}
                          </td>
                          <td className="px-3 py-2 text-[14px] font-['Arial',sans-serif] text-[#1d252c]">
                            {p.BelowMinimumPenalty
                              ? 'No penalty'
                              : formatMoney(p.PenaltyAmount, preview.Currency)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}

                {/* Explicit customer-confirmation gate (money movement). */}
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="penalty-confirmed"
                    checked={penaltyConfirmed}
                    onChange={() => setPenaltyConfirmed((c) => !c)}
                    className="size-[20px] accent-[#FF703E] cursor-pointer"
                  />
                  <label
                    htmlFor="penalty-confirmed"
                    className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] cursor-pointer select-none"
                  >
                    Penalty confirmed with customer and scripting completed.
                  </label>
                </div>

                {/* Step 3 — commit (only after the explicit confirmation). */}
                {penaltyConfirmed && (
                  <div className="mt-4">
                    <button
                      type="button"
                      disabled={submitting}
                      onClick={() => void completeForceWithdrawal()}
                      className="px-6 py-3 bg-[#FF703E] text-[#1D252C] rounded-md font-['Arial',sans-serif] font-bold text-[16px] hover:bg-[#e55a2a] focus:outline-none focus:ring-4 focus:ring-offset-2 focus:ring-[#005fcc] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {submitting ? 'Working…' : 'Complete force withdrawal'}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Force-withdrawal inline outcome (decline / transport error). */}
        {forceWithdrawal && fwError && !fwCompleted && (
          <div className="mb-6">
            <div
              id="force-withdrawal-error"
              role="alert"
              aria-live="assertive"
              className="max-w-[640px] p-4 bg-white border border-[#dc2626] font-['Arial',sans-serif] text-[14px] text-[#1d252c]"
            >
              {fwError}
            </div>
          </div>
        )}

        {/* Force-withdrawal success confirmation. */}
        {forceWithdrawal && fwCompleted && (
          <div
            id="force-withdrawal-success"
            className="py-4"
            role="status"
            aria-live="polite"
          >
            <p className="text-[18px] font-['Arial',sans-serif] font-bold text-[#16a34a] m-0">
              Force withdrawal completed successfully
            </p>
          </div>
        )}

        {/* Confirmation checkbox — gates the Complete button (CCF-69). Non-notice
            transfers only; a notice From account uses the force-withdrawal flow. */}
        {!forceWithdrawal && ready && !completed && (
          <div className="mb-6 flex items-center gap-3">
            <input
              type="checkbox"
              id="transfer-confirmed"
              checked={confirmed}
              onChange={() => setConfirmed((c) => !c)}
              className="size-[20px] accent-[#FF703E] cursor-pointer"
            />
            <label
              htmlFor="transfer-confirmed"
              className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] cursor-pointer select-none"
            >
              Transfer amounts confirmed and scripting completed.
            </label>
          </div>
        )}

        {/* Complete transfer. */}
        {!forceWithdrawal && ready && confirmed && !completed && (
          <div className="mb-6">
            <button
              type="button"
              disabled={submitting}
              onClick={() => void submit()}
              className="px-6 py-3 bg-[#FF703E] text-[#1D252C] rounded-md font-['Arial',sans-serif] font-bold text-[16px] hover:bg-[#e55a2a] focus:outline-none focus:ring-4 focus:ring-offset-2 focus:ring-[#005fcc] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Working…' : 'Complete transfer'}
            </button>
          </div>
        )}

        {/* Issue #115 — persistent inline outcome BESIDE the "Complete transfer"
            button. A failed transfer shows the gateway's Code + Message verbatim
            (recoverable — correct the accounts/amount + retry, #80/#82/#94); a
            success shows a definite confirmation. A toast alone auto-dismisses
            and is easy to miss. */}
        {!forceWithdrawal && transferError && !completed && (
          <div className="mb-6">
            <div
              id="transfer-error"
              role="alert"
              aria-live="assertive"
              className="max-w-[640px] p-4 bg-white border border-[#dc2626] font-['Arial',sans-serif] text-[14px] text-[#1d252c]"
            >
              {transferError}
            </div>
          </div>
        )}

        {/* Success confirmation (non-notice transfer). */}
        {!forceWithdrawal && completed && (
          <div
            id="transfer-success"
            className="py-4"
            role="status"
            aria-live="polite"
          >
            <p className="text-[18px] font-['Arial',sans-serif] font-bold text-[#16a34a] m-0">
              Transfer completed successfully
            </p>
          </div>
        )}
      </div>
    </main>
  );
}
